// Mirrors a horizontal scroller's position as a progress bar (used under the mobile product gallery).
class ScrollProgress extends HTMLElement {
  #abort = null;
  #frame = 0;

  connectedCallback() {
    const scope = this.closest(this.dataset.scope || 'body') || document.body;
    this.target = scope.querySelector(this.dataset.target);
    this.bar = this.querySelector('[data-progress-bar]');
    if (!this.target || (!this.bar && this.dataset.mode !== 'dashes')) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const schedule = () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#update());
    };
    this.target.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', schedule, { passive: true, signal });
    this.#update();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #update() {
    const { clientWidth } = this.target;
    let { scrollWidth, scrollLeft } = this.target;
    // s-gallery-loop adds one copied photo at each end; leave them out so the bar tracks only the real photos.
    if (this.target.dataset.loopOffset) {
      const slideSpan = this.target.firstElementChild?.getBoundingClientRect().width || 0;
      const gap = parseFloat(getComputedStyle(this.target).columnGap) || 0;
      const step = slideSpan + gap;
      scrollWidth -= step * 2;
      scrollLeft = Math.min(Math.max(Math.abs(scrollLeft) - step, 0), scrollWidth - clientWidth);
    }
    const max = scrollWidth - clientWidth;
    if (max <= 0) {
      this.hidden = true;
      return;
    }
    this.hidden = false;
    const thumb = clientWidth / scrollWidth;
    const ratio = Math.min(1, Math.abs(scrollLeft) / max);
    // data-mode="dashes": one [data-progress-dash] per slide; a single dash gets data-active, moving from the first
    // at the start of the scroll to the last at the end.
    if (this.dataset.mode === 'dashes') {
      const dashes = this.querySelectorAll('[data-progress-dash]');
      const progress = Math.min(1, Math.abs(scrollLeft) / max);
      const active = Math.round(progress * (dashes.length - 1));
      dashes.forEach((dash, index) => dash.toggleAttribute('data-active', index === active));
      return;
    }
    const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    // Translate is relative to the bar's own width, so scale the travel by the free track.
    const offset = ((ratio * (1 - thumb)) / thumb) * 100 * direction;
    this.bar.style.width = `${thumb * 100}%`;
    this.bar.style.transform = `translateX(${offset}%)`;
  }
}

if (!customElements.get('s-scroll-progress')) {
  customElements.define('s-scroll-progress', ScrollProgress);
}
